import { Add } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import _, { debounce } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import MedicationService from "../../services/medicationService";
import { IAppState } from "../../store/reducers";
import { AuthState } from "../../store/reducers/auth";
import { COLORS } from "../../styles/colors";
import Modal from "../Modal";
import {
  MedicationOption,
  PhotonMedication,
  SavedMedication,
  getOptionLabel,
  isCustomMedication,
  renderOption,
} from "./helper";

interface MedicationsFormProps {
  updateHistory?: (history: any) => void;
}

const MedicationsForm = ({ updateHistory }: MedicationsFormProps) => {
  const { authUser: user } = useSelector<IAppState, AuthState>((s) => s.auth);
  if (!user) return null;

  // State management
  const [isAddingMedication, setIsAddingMedication] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [savedMedications, setSavedMedications] = useState<SavedMedication[]>([]);
  const [medicationOptions, setMedicationOptions] = useState<PhotonMedication[]>([]);
  const [loading, setLoading] = useState(false);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [medicationIdToUpdate, setMedicationIdToUpdate] = useState<string>();
  const [medicationToDelete, setMedicationToDelete] =
    useState<SavedMedication | null>(null);

  // Form state
  const [query, setQuery] = useState("");
  const [selectedMedication, setSelectedMedication] = useState<MedicationOption | null>(null);
  const [strength, setStrength] = useState("");
  const [frequency, setFrequency] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);

  const fetchExistingMedications = async () => {
    try {
      const response = await MedicationService.getPatientMedications(user.id);
      setSavedMedications(response.data);
    } catch (error) {
      console.error("Error fetching medications:", error);
    } finally {
      setInitialLoading(false);
    }
  };

  // Fetch existing medications on component mount
  useEffect(() => {
    fetchExistingMedications();
  }, [user?.id]);

  useEffect(() => {
    if (needsUpdate && savedMedications && updateHistory) {
      updateHistory({ medications: { activeMedications: savedMedications } });
      setNeedsUpdate(false);
    }
  }, [savedMedications]);


  if (initialLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );

  // Search medications with debounce
  const searchMedications = debounce(async (searchQuery: string) => {
    setQuery(searchQuery);

    if (!searchQuery) {
      setMedicationOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await MedicationService.searchMedications(searchQuery);
      setMedicationOptions(response.data);
    } catch (error) {
      console.error("Error searching medications:", error);
    } finally {
      setLoading(false);
    }
  }, 300);

  const isFormValid = () =>
    [selectedMedication, strength, frequency, startDate].some((x) => !!x);

  const handleAdd = async () => {
    if (!isFormValid() || !selectedMedication) return;

    const newMedication: Omit<SavedMedication, "id"> = {
      name: selectedMedication.name,
      strength,
      frequency,
      startDate,
      patientId: user.id,
      ...(isCustomMedication(selectedMedication)
        ? {}
        : _.pick(selectedMedication, [
            "photonMedicationId",
            "brandName",
            "genericName",
            "form",
            "description",
          ])),
    };

    setNeedsUpdate(true);
    await MedicationService.addMedication(user.id, newMedication);
    await fetchExistingMedications();

    // Reset form
    setQuery("");
    setMedicationOptions([]);
    setSelectedMedication(null);
    setStrength("");
    setFrequency("");
    setStartDate(null);
    setIsAddingMedication(false);
  };

  const handleEdit = (medication: SavedMedication) => {
    setSelectedMedication(medication);
    setMedicationIdToUpdate(medication.id);
    setStrength(medication.strength || "");
    setFrequency(medication.frequency || "");
    setStartDate(medication.startDate || null);
    setIsEditMode(true);
    setIsAddingMedication(true);
  };

  const handleDelete = async () => {
    if (!medicationToDelete) return;

    try {
      setNeedsUpdate(true);
      await MedicationService.deleteMedication(user.id, medicationToDelete.id);
      await fetchExistingMedications();
      setDeleteModalOpen(false);
      setMedicationToDelete(null);
    } catch (error) {
      console.error("Error deleting medication:", error);
    }
  };

  const handleUpdate = async () => {
    if (!isFormValid() || !selectedMedication || !medicationIdToUpdate) return;

    const updatedMedication: SavedMedication = {
      ...selectedMedication,
      id: medicationIdToUpdate,
      strength,
      frequency,
      startDate,
      patientId: user.id,
    };

    try {
      setNeedsUpdate(true);
      await MedicationService.updateMedication(user.id, updatedMedication);
      await fetchExistingMedications();

      handleCancel();
    } catch (error) {
      console.error("Error updating medication:", error);
    }
  };

  const handleCancel = () => {
    setQuery("");
    setMedicationOptions([]);
    setSelectedMedication(null);
    setStrength("");
    setFrequency("");
    setStartDate(null);
    setIsAddingMedication(false);
    setIsEditMode(false);
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto" }}>
      {/* Header */}
      {!isAddingMedication && (
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
            gap: 1,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Medications
          </Typography>
          <Typography variant="body1">
            If you are currently taking any medications, please add them below.
          </Typography>
        </Box>
      )}

      {/* Medications List Panel */}
      {!isAddingMedication && (
        <Card sx={{ mt: 1, p: 1 }}>
          <CardContent
            sx={{
              display: "flex",
              gap: 1,
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {savedMedications.length > 0 ? (
              <Box
                sx={{
                  mb: 1,
                  gap: 1,
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => setIsAddingMedication(true)}
                  className="green-action-btn"
                  sx={{ alignSelf: "flex-end", display: "flex", gap: 0.5 }}
                >
                  <Add sx={{ color: "primary.contrastText" }} />
                  Add Medication
                </Button>
                {savedMedications.map((med, index) => (
                  <Box
                    key={index}
                    sx={{
                      py: 1,
                      px: 2,
                      bgcolor: "grey.200",
                      borderRadius: "10px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {getOptionLabel(med)}
                      </Typography>
                      <Typography variant="body2">
                        Dosage: {med.strength}
                      </Typography>
                      <Typography variant="body2">
                        Frequency: {med.frequency}
                      </Typography>
                      {med.endDate ? (
                        <Typography variant="body2">
                          Taken from:{" "}
                          {med.startDate
                            ? moment(med.startDate).format("MM/DD/YYYY")
                            : "N/A"}{" "}
                          -{" "}
                          {med.endDate
                            ? moment(med.endDate).format("MM/DD/YYYY")
                            : "N/A"}
                        </Typography>
                      ) : (
                        <Typography variant="body2">
                          Started:{" "}
                          {med.startDate
                            ? moment(med.startDate).format("MM/DD/YYYY")
                            : "N/A"}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                    >
                      <IconButton onClick={() => handleEdit(med)} size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setMedicationToDelete(med);
                          setDeleteModalOpen(true);
                        }}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography variant="body1" color={COLORS.GREY}>
                  No medications added yet
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setIsAddingMedication(true)}
                  size="large"
                >
                  Add Medication
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Medication Form Panel */}
      {isAddingMedication && (
        <Card sx={{ mt: 1, p: 1 }}>
          <CardContent>
            <Stack direction="column" spacing={1}>
              <Typography variant="h5" fontWeight="bold" textAlign="center">
                {isEditMode ? "Edit Medication" : "Add New Medication"}
              </Typography>

              <Autocomplete<MedicationOption, false, false, false>
                options={[
                  // Add custom option as first option if there's input
                  ...(query
                    ? [{ name: query, isCustomOption: true as const }]
                    : []),
                  ...medicationOptions,
                ]}
                getOptionLabel={getOptionLabel}
                loading={loading}
                value={selectedMedication}
                onChange={(_, value) => {
                  setSelectedMedication(value);
                  if (value && !isCustomMedication(value)) {
                    setStrength(value.strength || "");
                  }
                }}
                onInputChange={(_, value) => searchMedications(value)}
                noOptionsText={
                  !query.length
                    ? "Type to search for medications"
                    : "No options"
                }
                renderOption={renderOption}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Medication Name"
                    sx={{ marginY: 0 }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <TextField
                label="Dosage"
                value={strength}
                onChange={(e) => setStrength(e.target.value)}
                fullWidth
              />

              <TextField
                label="How often do you take it?"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                fullWidth
              />

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disableFuture={true}
                  label="Start Date"
                  value={startDate ? dayjs(startDate) : null}
                  onChange={(newValue) =>
                    setStartDate(newValue ? newValue.toDate() : null)
                  }
                  sx={{ flexGrow: 1 }}
                />
              </LocalizationProvider>
            </Stack>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 2,
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                onClick={isEditMode ? handleUpdate : handleAdd}
                disabled={!isFormValid()}
                sx={{
                  width: "fit-content",
                  px: 4,
                }}
              >
                {isEditMode ? "Update" : "Save"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  width: "fit-content",
                  px: 4,
                }}
              >
                Cancel
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        children={
          <Box>
            <DialogTitle>
              <Typography variant="h5" color={COLORS.DARK_GREY}>
                Delete Medication
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" color={COLORS.GREY}>
                Are you sure you want to delete{" "}
                {getOptionLabel(medicationToDelete)}? This action cannot be
                undone.
              </Typography>
            </DialogContent>
          </Box>
        }
        buttonActions={[
          {
            text: "Cancel",
            style: "cancel",
            action: () => setDeleteModalOpen(false),
          },
          {
            text: "Delete",
            style: "confirm",
            action: handleDelete,
          },
        ]}
      />
    </Box>
  );
};

export default MedicationsForm;
